import {I18nManager} from 'react-native';
import {getLocalData} from './storage/storage';
import translations from '../translations';
import ExistLanguages from '../translations/loaders';
import {keys} from '../api/keys';
import {saveLanguage} from '../screens/content/languages/action';
import {languagesData} from '../assets/static/languages';
import {LanguageItemType} from '../interfaces/connected-components/languagePickerModal';

/**
 * Initialize the language settings for the application based on the stored language data.
 * If no language is stored, the default language is set to English.
 * This function sets up language-related configurations, such as RTL support.
 *
 * @returns {Promise<void>} - A Promise that resolves once the language is initialized.
 */
export const initLanguage = async (): Promise<boolean> => {
  // Retrieve the stored language data from local storage
  const language = getLocalData(keys.LANG);
  // Save the language and set up translations
  saveLanguage(language || ExistLanguages.en);
  translations(language?.languageCode || 'en');

  // Configure Right-to-Left (RTL) support based on the stored language
  I18nManager.allowRTL(language?.languageCode === 'ar');
  I18nManager.forceRTL(language?.languageCode === 'ar');
  I18nManager.swapLeftAndRightInRTL(language?.languageCode === 'ar');
  return true;
};

export const searchAndFilterLanguages = (
  searchName: string = '',
  languageCodes: string[] | null = null,
  tempLanguages: LanguageItemType[] | null = null,
): LanguageItemType[] => {
  const lowerCaseSearchName = searchName.toLowerCase().trim();

  // Use tempLanguages if provided, otherwise fall back to the LANGUAGES array
  let languagesArray = (
    tempLanguages ? tempLanguages : languagesData
  ) as LanguageItemType[];

  // If searchName is empty and languageCodes is null/undefined, return all languages
  if (!lowerCaseSearchName && (!languageCodes || languageCodes.length === 0)) {
    return languagesArray;
  }

  let filteredLanguages = languagesArray;

  // Filter by language codes if provided and tempLanguages is not used
  if (!tempLanguages && languageCodes && languageCodes.length > 0) {
    filteredLanguages = languageCodes
      .map(code => languagesData.find(language => language.code === code))
      .filter(language => language !== undefined) as LanguageItemType[];
  }

  // Further filter by searchName if provided
  if (lowerCaseSearchName) {
    filteredLanguages = filteredLanguages.filter(language =>
      language.name.toLowerCase().includes(lowerCaseSearchName),
    );
  }

  return filteredLanguages;
};
